import React, { createContext, useContext, useState, useCallback } from 'react';
import { searchService, type SearchParams, type SearchResult, type SearchFilters } from '../services/search.service';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Meta {
  searchTerm: string | null;
  location: {
    latitude: number;
    longitude: number;
    radius: number;
  } | null;
  filters: SearchFilters;
  sortBy: string;
}

interface SearchContextType {
  results: SearchResult[];
  loading: boolean;
  error: string | null;
  pagination: Pagination | null;
  meta: Meta | null;
  search: (params: SearchParams) => Promise<void>;
  loadMore: () => Promise<void>;
  clearResults: () => void;
  setFilters: (filters: SearchFilters) => void;
  filters: SearchFilters;
  sortBy: string;
  setSortBy: (sort: string) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [sortBy, setSortBy] = useState('relevance');
  const [currentParams, setCurrentParams] = useState<SearchParams>({});

  const search = useCallback(async (params: SearchParams) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await searchService.search(params);
      setResults(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
      if (response.meta) {
        setMeta(response.meta);
      }
      setCurrentParams({ ...params, page: 1 });
    } catch (err: any) {
      setError(err.message || 'Search failed');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!pagination || pagination.page >= pagination.pages) return;
    
    setLoading(true);
    try {
      const nextPage = pagination.page + 1;
      const response = await searchService.search({
        ...currentParams,
        page: nextPage,
      });
      setResults(prev => [...prev, ...response.data]);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load more');
    } finally {
      setLoading(false);
    }
  }, [pagination, currentParams]);
  

  const clearResults = useCallback(() => {
    setResults([]);
    setPagination(null);
    setMeta(null);
    setError(null);
  }, []);

  return (
    <SearchContext.Provider
      value={{
        results,
        loading,
        error,
        pagination,
        meta,
        search,
        loadMore,
        clearResults,
        filters,
        setFilters,
        sortBy,
        setSortBy,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};